/**
 * A menu you can actually read, for a list whose length nobody controls.
 *
 * THE FIRST FLOATING LAYER IN THE APP, and it exists because the native `<select>` it
 * replaces could not grow up. A `select` is the right answer for the ten controls still
 * using `ui.tsx`'s `Select` - a timezone, a day's opening hour, a category to merge into -
 * because those lists are fixed, known, and picked from once. The agent switcher is
 * none of those things: it is six rows on a database with two organizations and it is the
 * control this product is navigated by. It needed to be searchable, it needed to show an
 * organization's name next to its agent's, and a platform menu can do neither.
 *
 * SIZED BY THE LIST RATHER THAN BY A GUESS. Two agents is a two-row menu with no scrollbar
 * and no search box; fifty is a scrolling panel with a filter at the top. The threshold is
 * one constant and the two shapes are the same component, so the small case never pays for
 * the large one - a search field over a menu of two is furniture, not a feature.
 *
 * IT DOES NOT FLOAT, because nothing in this product does. There are no shadow tokens (see
 * the note in `styles.css`) and a panel is separated from what it covers by its border and
 * its ground, exactly like every other surface here.
 *
 * WHAT IT IS NOT: a combobox you can type free text into, and not a multi-select. The value
 * is always one of `choices`, which is what lets the trigger render the selection as
 * whatever the caller likes rather than as a string this file formats.
 */

import { useEffect, useId, useMemo, useRef, useState } from "react";
import type { ReactNode } from "react";
import { CONTROL_BOX, cx } from "../ui";
import { ChevronDown, Search, Tick } from "./Icons";

export interface Choice {
  value: string;
  /** The row's first line. */
  label: string;
  /** A second, dimmer line - who a thing belongs to, or what it is called on the wire. */
  note?: string;
  /** Matched by the filter but never printed. */
  keywords?: string[];
}

/**
 * Above this many rows the panel grows a filter.
 *
 * Eight is where a menu stops being something you take in at a glance and starts being
 * something you look through. It is also about where the panel hits its scroll cap, so the
 * filter appears at the same moment the list stops being fully visible - one change, not two.
 */
const FILTER_FROM = 8;

/**
 * A row at the foot of the panel that does something rather than choosing a value - "New
 * agent" under the list of agents. It is the last stop of the arrow keys, so the keyboard
 * reaches it the way a mouse does.
 */
export interface ListboxAction {
  label: string;
  icon?: ReactNode;
  onSelect: () => void;
}

export function Listbox({
  value,
  choices,
  onChange,
  ariaLabel,
  className,
  children,
  opens = "down",
  action,
  rootClassName,
  panelClassName = "min-w-72",
}: {
  value: string | null;
  choices: Choice[];
  onChange: (value: string) => void;
  ariaLabel: string;
  /** Classes for the trigger, which is the caller's card and not this file's business. */
  className?: string;
  /** What the closed trigger shows. */
  children: ReactNode;
  /**
   * Which way the panel drops. The caller's to say, because only the caller knows which
   * edge of the viewport its trigger sits against. See the note on the panel below.
   */
  opens?: "up" | "down";
  action?: ListboxAction;
  /** Classes for the wrapper, which is the box the panel is anchored to. */
  rootClassName?: string;
  /** The panel's minimum width. An agent's name and its organization's need 18rem; a picker's rows need only the trigger's. */
  panelClassName?: string;
}) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [active, setActive] = useState(0);

  const root = useRef<HTMLDivElement>(null);
  const trigger = useRef<HTMLButtonElement>(null);
  const field = useRef<HTMLInputElement>(null);
  const id = useId();

  const searchable = choices.length > FILTER_FROM;

  const matches = useMemo(() => {
    const needle = query.trim().toLowerCase();
    if (!needle) return choices;
    return choices.filter((choice) =>
      [choice.label, choice.note, ...(choice.keywords ?? [])]
        .filter(Boolean)
        .some((text) => text!.toLowerCase().includes(needle)),
    );
  }, [choices, query]);

  // Opening starts from the selection rather than from the top: the row you are on is the
  // one you are most likely to be moving away from by one step.
  useEffect(() => {
    if (!open) return;
    setQuery("");
    const selected = choices.findIndex((choice) => choice.value === value);
    setActive(selected < 0 ? 0 : selected);
    field.current?.focus();
  }, [open, choices, value]);

  // The cursor's stops: every match, then the action if there is one.
  const stops = matches.length + (action ? 1 : 0);

  // A filtered list can be shorter than the cursor sitting in it.
  useEffect(() => {
    setActive((current) => (current > stops - 1 ? 0 : current));
  }, [stops]);

  useEffect(() => {
    if (!open) return;
    document.getElementById(`${id}-${active}`)?.scrollIntoView({ block: "nearest" });
  }, [open, active, id]);

  /*
   * `pointerdown` rather than `click`, so a press that begins outside the panel closes it
   * before it can land on whatever is underneath. The `click` version of this lets you
   * close the menu and press a button in the same gesture, which nobody means to do.
   */
  useEffect(() => {
    if (!open) return;
    const dismiss = (event: PointerEvent) => {
      if (!root.current?.contains(event.target as Node)) setOpen(false);
    };
    document.addEventListener("pointerdown", dismiss);
    return () => document.removeEventListener("pointerdown", dismiss);
  }, [open]);

  const close = () => {
    setOpen(false);
    trigger.current?.focus();
  };

  const choose = (choice: Choice) => {
    onChange(choice.value);
    close();
  };

  const runAction = () => {
    setOpen(false);
    action?.onSelect();
  };

  /*
   * One handler on the wrapper for both shapes of the panel. With a filter, focus is in the
   * input and these bubble up from it; without one, focus never leaves the trigger and they
   * arrive from there. Either way the cursor is `active` and the focused element is only
   * ever where you type - which is why `aria-activedescendant` is on both.
   */
  const onKeyDown = (event: React.KeyboardEvent) => {
    if (!open) {
      if (event.key === "ArrowDown" || event.key === "Enter" || event.key === " ") {
        event.preventDefault();
        setOpen(true);
      }
      return;
    }
    switch (event.key) {
      case "ArrowDown":
        event.preventDefault();
        setActive((current) => (stops ? (current + 1) % stops : 0));
        break;
      case "ArrowUp":
        event.preventDefault();
        setActive((current) => (stops ? (current - 1 + stops) % stops : 0));
        break;
      case "Home":
        event.preventDefault();
        setActive(0);
        break;
      case "End":
        event.preventDefault();
        setActive(Math.max(stops - 1, 0));
        break;
      case "Enter": {
        event.preventDefault();
        const choice = matches[active];
        if (choice) choose(choice);
        else if (action && active === matches.length) runAction();
        break;
      }
      case "Escape":
        event.preventDefault();
        close();
        break;
      case "Tab":
        setOpen(false);
        break;
    }
  };

  const listbox = `${id}-listbox`;

  return (
    <div ref={root} className={cx("relative", rootClassName)} onKeyDown={onKeyDown} data-popup-open={open || undefined}>
      <button
        ref={trigger}
        type="button"
        role="combobox"
        aria-label={ariaLabel}
        aria-expanded={open}
        aria-haspopup="listbox"
        aria-controls={open ? listbox : undefined}
        aria-activedescendant={open && !searchable ? `${id}-${active}` : undefined}
        onClick={() => setOpen((was) => !was)}
        className={className}
      >
        {children}
      </button>

      {open && (
        <div
          /*
           * ONE ANCHOR, NO MEASURING, NO COLLISION LIBRARY, and the direction is a prop
           * rather than a guess. This used to open upward and only upward, because its one
           * trigger sat in the rail's footer a few pixels off the bottom of the viewport. The
           * rail became a top bar and the same trigger now sits a few pixels off the TOP, so
           * a panel that rose would open off-screen instead - `top-full` is the whole of
           * the positioning logic for that case, as `bottom-full` was for the other.
           */
          className={cx(
            "absolute left-0 z-20 w-full",
            opens === "up" ? "bottom-full mb-1.5" : "top-full mt-1.5",
            // At least as wide as the trigger, which is what makes it read as that card
            // opening rather than as a second thing arriving next to it - and never
            // narrower than an agent's name and its organization's, which a trigger that is only
            // a name in a bar can be.
            panelClassName,
            "overflow-hidden rounded-md border border-line bg-paper",
          )}
        >
          {searchable && (
            <div className="relative border-b border-line-soft">
              <Search
                size={15}
                className="pointer-events-none absolute top-1/2 left-2.5 -translate-y-1/2 text-ink-ghost"
              />
              <input
                ref={field}
                type="text"
                value={query}
                onChange={(event) => setQuery(event.target.value)}
                placeholder="Search"
                aria-label={`Search ${ariaLabel.toLowerCase()}`}
                aria-controls={listbox}
                aria-activedescendant={`${id}-${active}`}
                // The panel's own border is the field's border; a second one inside it
                // would draw a box in a box.
                className="w-full bg-transparent py-2 pr-2.5 pl-[30px] text-sm text-ink outline-none placeholder:text-ink-ghost"
              />
            </div>
          )}

          <ul
            id={listbox}
            role="listbox"
            aria-label={ariaLabel}
            className="max-h-[320px] overflow-y-auto p-1"
          >
            {matches.map((choice, index) => {
              const selected = choice.value === value;
              return (
                <li key={choice.value}>
                  <div
                    id={`${id}-${index}`}
                    role="option"
                    aria-selected={selected}
                    onClick={() => choose(choice)}
                    onPointerMove={() => setActive(index)}
                    className={cx(
                      "flex cursor-pointer items-center gap-2 rounded-xs px-2 py-1.5",
                      // Two signals for two different things: the tick is what is chosen,
                      // the wash is where the cursor is. A row that used one for both
                      // would say you had selected whatever you last arrowed past.
                      index === active && "bg-line-soft",
                    )}
                  >
                    <span className="grid min-w-0 flex-1 leading-tight">
                      <span
                        className={cx(
                          "truncate text-sm",
                          selected ? "font-[550] text-ink" : "text-ink",
                        )}
                      >
                        {choice.label}
                      </span>
                      {choice.note && (
                        <span className="truncate text-sm text-ink">{choice.note}</span>
                      )}
                    </span>
                    {selected && <Tick size={15} className="flex-none text-ink" />}
                  </div>
                </li>
              );
            })}

            {matches.length === 0 && (
              <li className="px-2 py-3 text-center text-sm text-ink">Nothing matches</li>
            )}
          </ul>

          {/* UNDER THE SCROLLER, NOT IN IT: at the foot of a long list it would be the one
              row nobody scrolls down to find. */}
          {action && (
            <div className="border-t border-line-soft p-1">
              <div
                id={`${id}-${matches.length}`}
                role="button"
                tabIndex={-1}
                onClick={runAction}
                onPointerMove={() => setActive(matches.length)}
                className={cx(
                  "flex cursor-pointer items-center gap-2 rounded-xs px-2 py-1.5 text-sm text-ink",
                  "[&_svg]:flex-none",
                  active === matches.length && "bg-line-soft",
                )}
              >
                {action.icon}
                {action.label}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

/**
 * A `Select` that opens this file's panel instead of the platform's menu.
 *
 * WHY NOT THE NATIVE ONE, for the lists inside a dialog: the platform draws its own menu,
 * in the system font, with a blue highlight, at the system's scale - and in a zoomed or
 * embedded browser it can open a hand's width away from the box it belongs to (Illia's
 * screenshot of the tool editor, 2026-09-29). This one looks like the control it came from
 * and opens directly under it. Short fixed lists only: no filter below `FILTER_FROM` rows.
 */
export function Picker<T extends string>({
  value,
  options,
  onChange,
  label,
  placeholder,
  className,
  rootClassName,
}: {
  value: T;
  options: Array<{ value: T; label: string; note?: string }>;
  onChange: (next: T) => void;
  /** The accessible name, as `aria-label` was on the select. */
  label: string;
  /** Shown when `value` matches no option. */
  placeholder?: string;
  /** Classes for the box: a width, or `font-mono`. */
  className?: string;
  /** Classes for the wrapper, when the box sits in a flex row and must not stretch. */
  rootClassName?: string;
}) {
  const current = options.find((option) => option.value === value);
  return (
    <Listbox
      value={value}
      choices={options}
      onChange={(next) => onChange(next as T)}
      ariaLabel={label}
      rootClassName={rootClassName}
      panelClassName="min-w-full"
      className={cx(
        CONTROL_BOX,
        "flex w-full cursor-pointer items-center justify-between gap-2 text-left hover:border-line-strong aria-expanded:border-line-strong",
        className,
      )}
    >
      <span className="truncate">{current?.label ?? placeholder ?? ""}</span>
      <ChevronDown size={14} className="flex-none" />
    </Listbox>
  );
}
